import type { OfferDetail } from '@fos/shared/contracts/offers';
import { todayLocal } from '@fos/shared/time';
import { type FormEvent, useEffect, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Textarea } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';

export type OfferOutcome = 'accepted' | 'rejected';

const COPY: Record<OfferOutcome, { title: string; action: string; done: string; after: string }> = {
  accepted: {
    title: 'Oznacz ofertę jako przyjętą',
    action: 'Oznacz jako przyjętą',
    done: 'Oferta przyjęta',
    after: 'Oferta stanie się główną ofertą klienta (godziny w profilu).',
  },
  rejected: {
    title: 'Oznacz ofertę jako odrzuconą',
    action: 'Oznacz jako odrzuconą',
    done: 'Oferta odrzucona',
    after: 'Poprawioną ofertę przygotujesz jako nową wersję.',
  },
};

/** Opis skutku: zamknięcie bieżącej wersji albo zmiana wcześniej zapisanego wyniku (pomyłka, zmiana decyzji). */
function outcomeNote(offer: OfferDetail, outcome: OfferOutcome): string {
  if (offer.status === 'accepted' || offer.status === 'rejected') {
    const from = offer.status === 'accepted' ? 'Przyjęta' : 'Odrzucona';
    const to = outcome === 'accepted' ? 'Przyjęta' : 'Odrzucona';
    const main =
      offer.status === 'accepted' ? ' Oferta przestanie być główną ofertą klienta.' : ` ${COPY.accepted.after}`;
    return `Zmienisz wynik z „${from}” na „${to}”.${main}`;
  }
  return `Wersja ${offer.currentVersion} zostanie zamknięta. ${COPY[outcome].after}`;
}

/** Ręczne przyjęcie albo odrzucenie oferty przez klienta (MVP §8): data (domyślnie dziś) i opcjonalna notatka. */
export function OutcomeDialog({
  offer,
  outcome,
  open,
  onOpenChange,
}: {
  offer: OfferDetail;
  outcome: OfferOutcome;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const accept = trpc.offers.markAccepted.useMutation();
  const reject = trpc.offers.markRejected.useMutation();
  const mutation = outcome === 'accepted' ? accept : reject;
  const copy = COPY[outcome];
  const [date, setDate] = useState(todayLocal());
  const [note, setNote] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDate(todayLocal());
    setNote('');
    setDateError(null);
    setFormError(null);
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!date) {
      setDateError('Podaj datę');
      return;
    }
    if (date > todayLocal()) {
      setDateError('Data nie może być w przyszłości.');
      return;
    }
    try {
      await mutation.mutateAsync({
        id: offer.id,
        version: offer.version,
        date,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(copy.done);
      await utils.offers.invalidate();
      onOpenChange(false);
    } catch (err) {
      if (appErrorCode(err) === 'VALIDATION_ERROR') {
        const issue = validationIssues(err).find((i) => i.path === 'date');
        if (issue) {
          setDateError(issue.message);
          return;
        }
      }
      setFormError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      description={outcomeNote(offer, outcome)}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="offer-outcome-form" loading={mutation.isPending}>
            {copy.action}
          </Button>
        </>
      }
    >
      <form id="offer-outcome-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label={outcome === 'accepted' ? 'Data przyjęcia' : 'Data odrzucenia'} error={dateError}>
          <DateInput value={date} onValueChange={setDate} max={todayLocal()} />
        </Field>
        <Field label="Notatka (opcjonalnie)" hint="np. jak klient odpowiedział">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
